"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

import { cn } from "@/lib/utils";

type Heading = { id: string; text: string; level: number };

/**
 * "On this page" rail. Scans the rendered article for headings that
 * `rehype-slug` gave ids to, then highlights the one in view. The scan is
 * keyed on the pathname so it re-runs when navigating between doc pages,
 * and the read is deferred to the next frame so the new article is in the
 * DOM first.
 */
export function TableOfContents() {
  const pathname = usePathname();
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    let observer: IntersectionObserver | null = null;

    const raf = requestAnimationFrame(() => {
      const article = document.querySelector("[data-docs-article]");
      const nodes = article
        ? Array.from(article.querySelectorAll<HTMLElement>("h2[id], h3[id]"))
        : [];

      setHeadings(
        nodes.map((n) => ({
          id: n.id,
          text: n.textContent ?? "",
          level: n.tagName === "H2" ? 2 : 3,
        })),
      );
      setActiveId("");

      if (nodes.length === 0 || typeof IntersectionObserver === "undefined") {
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort(
              (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
            );
          if (visible[0]) setActiveId(visible[0].target.id);
        },
        { rootMargin: "0px 0px -70% 0px", threshold: 0 },
      );
      for (const n of nodes) observer.observe(n);
    });

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [pathname]);

  if (headings.length === 0) return null;

  return (
    <nav aria-label="On this page" className="flex flex-col gap-2">
      <div className="text-rl-text3 text-[0.7rem] font-semibold tracking-[0.08em] uppercase">
        On this page
      </div>
      <ul className="flex flex-col gap-1 text-[0.8rem]">
        {headings.map((h) => (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              className={cn(
                "block border-l-2 py-0.5 transition-colors",
                h.level === 3 ? "pl-5" : "pl-3",
                activeId === h.id
                  ? "border-rl-accent text-rl-accent"
                  : "border-rl-border text-rl-text3 hover:text-rl-text2",
              )}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
